import { Building2, ChevronDown, ChevronLeft, ChevronRight, Plus, Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";

import AddOrganizationDrawer from "../components/AddOrganizationDrawer";
import { getConfigWithToken } from "../config/config";
import { getSuperAdminOrganizations } from "../config/url";
import classes from "./SuperAdminOrganizations.module.css";

const DEFAULT_PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 500;
const STATUS_ALL = "all";
const STATUS_ACTIVE = "active";
const STATUS_INACTIVE = "inactive";
const DEFAULT_STATUS_FILTER = STATUS_ALL;

const formatLocation = (city, state) => {
	const parts = [city, state].filter(Boolean);
	return parts.length ? parts.join(", ") : "—";
};

const SuperAdminOrganizations = () => {
	const navigate = useNavigate();
	const token = useSelector((state) => state?.user?.user?.token) || localStorage.getItem("user_id");

	const [addDrawerOpen, setAddDrawerOpen] = useState(false);
	const [activeCount, setActiveCount] = useState(0);
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [hasError, setHasError] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [page, setPage] = useState(1);
	const [rows, setRows] = useState([]);
	const [search, setSearch] = useState("");
	const [statusFilter, setStatusFilter] = useState(DEFAULT_STATUS_FILTER);
	const [totalItems, setTotalItems] = useState(0);
	const [totalPages, setTotalPages] = useState(1);

	const requestConfig = useMemo(
		() => ({
			headers: {
				Accept: "application/json",
				"Content-Type": "application/json",
				...(token ? getConfigWithToken(token).headers : {}),
			},
		}),
		[token],
	);

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [search]);

	useEffect(() => {
		setPage(1);
	}, [debouncedSearch, statusFilter]);

	const fetchRows = useCallback(async () => {
		setIsLoading(true);
		setHasError(false);
		try {
			const response = await axios.get(getSuperAdminOrganizations, {
				...requestConfig,
				params: {
					limit: DEFAULT_PAGE_SIZE,
					page,
					status: statusFilter,
					...(debouncedSearch ? { search: debouncedSearch } : {}),
				},
			});
			const pagination = response?.data?.pagination || {};
			setRows(Array.isArray(response?.data?.data) ? response.data.data : []);
			setActiveCount(response?.data?.meta?.activeCount ?? 0);
			setTotalItems(pagination.total || 0);
			setTotalPages(Math.max(1, pagination.totalPages || 1));
		} catch {
			setHasError(true);
			setRows([]);
			setActiveCount(0);
			setTotalItems(0);
			setTotalPages(1);
		} finally {
			setIsLoading(false);
		}
	}, [debouncedSearch, page, requestConfig, statusFilter]);

	useEffect(() => {
		fetchRows();
	}, [fetchRows]);

	const hasFiltersApplied = Boolean(debouncedSearch.trim()) || statusFilter !== DEFAULT_STATUS_FILTER;

	const handleClearFilters = () => {
		setSearch("");
		setStatusFilter(STATUS_ALL);
		setPage(1);
	};

	const emptyMessage = hasFiltersApplied
		? "No organizations match your search or filters."
		: "No organizations found.";

	return (
		<div className={classes.page}>
			<div className={classes.pageHeader}>
				<div className={classes.titleBlock}>
					<h1 className={classes.title}>Organizations</h1>
					<p className={classes.subtitle}>
						{isLoading && !rows.length ? "Loading..." : `${activeCount} active`}
					</p>
				</div>
			</div>

			<div className={classes.filtersRow}>
				<div className={classes.filtersLeft}>
					<div className={classes.searchWrap}>
						<Search aria-hidden="true" className={classes.searchIcon} size={15} strokeWidth={2} />
						<input
							aria-label="Search organizations"
							className={classes.searchInput}
							onChange={(event) => setSearch(event.target.value)}
							placeholder="Search organizations..."
							type="search"
							value={search}
						/>
					</div>

					<div className={classes.selectWrap}>
						<select
							aria-label="Filter by status"
							className={classes.filterSelect}
							onChange={(event) => setStatusFilter(event.target.value)}
							value={statusFilter}>
							<option value={STATUS_ALL}>Status</option>
							<option value={STATUS_ACTIVE}>Active</option>
							<option value={STATUS_INACTIVE}>Inactive</option>
						</select>
						<ChevronDown aria-hidden="true" className={classes.selectIcon} size={15} strokeWidth={2} />
					</div>

					{hasFiltersApplied ? (
						<button className={classes.clearButton} onClick={handleClearFilters} type="button">
							Clear Filters
						</button>
					) : null}
				</div>

				<button
					className={classes.addButton}
					onClick={() => setAddDrawerOpen(true)}
					type="button">
					<Plus size={15} strokeWidth={2.5} />
					Add Organization
				</button>
			</div>

			<AddOrganizationDrawer
				isOpen={addDrawerOpen}
				onClose={() => setAddDrawerOpen(false)}
				onSuccess={() => fetchRows()}
			/>

			{hasError ? (
				<div className={classes.errorBox}>
					<p>Organizations could not be loaded.</p>
					<button className={classes.retryButton} onClick={fetchRows} type="button">
						Retry
					</button>
				</div>
			) : (
				<>
					<div className={classes.tableWrap}>
						<table className={classes.table}>
							<thead>
								<tr>
									<th>Organization</th>
									<th>Location</th>
									<th>Users</th>
									<th>Status</th>
								</tr>
							</thead>
							<tbody>
								{isLoading ? (
									<tr>
										<td className={classes.empty} colSpan={4}>
											Loading...
										</td>
									</tr>
								) : rows.length === 0 ? (
									<tr>
										<td className={classes.empty} colSpan={4}>
											{emptyMessage}
										</td>
									</tr>
								) : (
									rows.map((organization) => (
										<tr
											className={classes.row}
											key={organization.id}
											onClick={() => navigate(`/organizations/${organization.id}`)}>
											<td>
												<div className={classes.orgCell}>
													<span aria-hidden="true" className={classes.orgIcon}>
														<Building2 size={16} strokeWidth={1.75} />
													</span>
													<span className={classes.orgName}>{organization.name || "—"}</span>
												</div>
											</td>
											<td className={classes.cellMuted}>
												{formatLocation(organization.city, organization.state)}
											</td>
											<td className={classes.cellMuted}>{organization.active_users ?? 0}</td>
											<td>
												<span
													className={`${classes.badge} ${
														organization.is_active ? classes.badgeActive : classes.badgeInactive
													}`}>
													{organization.is_active ? "Active" : "Inactive"}
												</span>
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>

					{totalPages > 1 ? (
						<div className={classes.pagination}>
							<span className={classes.pageSummary}>
								Page {page} of {totalPages} ({totalItems} total)
							</span>
							<button
								aria-label="Previous page"
								className={classes.pageButton}
								disabled={page <= 1}
								onClick={() => setPage((current) => current - 1)}
								type="button">
								<ChevronLeft size={16} />
							</button>
							<button
								aria-label="Next page"
								className={classes.pageButton}
								disabled={page >= totalPages}
								onClick={() => setPage((current) => current + 1)}
								type="button">
								<ChevronRight size={16} />
							</button>
						</div>
					) : null}
				</>
			)}
		</div>
	);
};

export default SuperAdminOrganizations;
